import { useState } from "react";
import { X } from "lucide-react";
import type { Folder } from "@onemileauto/shared-types";
import { MAX_FOLDER_NAME_LENGTH } from "@onemileauto/shared-types";
import { Button } from "@onemileauto/ui";
import { ApiError } from "../../lib/api-client";
import { useCreateFolder, useDeleteFolder, useRenameFolder } from "../../hooks/use-folders";

interface FolderEditorSheetProps {
  mailboxId: string;
  /** Omit to create a new folder; pass an existing (non-system) folder to rename/delete it. */
  folder?: Folder;
  onClose: () => void;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/**
 * One sheet handles both "New folder" (folder omitted) and "Manage folder"
 * (rename + delete, folder passed in) — same full-screen-on-mobile/
 * centered-card-on-desktop shell as compose-sheet.tsx, so folder management
 * feels like the same product as everything else rather than a bolted-on
 * settings panel. Only reachable for custom folders — see folder-nav.tsx,
 * which never renders the trigger for a system folder — but this also
 * can't literally submit a rename/delete for one, since MailboxDO rejects
 * it server-side regardless.
 */
export function FolderEditorSheet({ mailboxId, folder, onClose }: FolderEditorSheetProps) {
  const [name, setName] = useState(folder?.name ?? "");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const createFolder = useCreateFolder(mailboxId);
  const renameFolder = useRenameFolder(mailboxId);
  const deleteFolder = useDeleteFolder(mailboxId);

  const isEditing = Boolean(folder);
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_FOLDER_NAME_LENGTH;
  const saving = createFolder.isPending || renameFolder.isPending;
  const submitError = createFolder.error ?? renameFolder.error;

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      if (isEditing && folder) {
        if (trimmed === folder.name) {
          onClose();
          return;
        }
        await renameFolder.mutateAsync({ folderId: folder.id, name: trimmed });
      } else {
        await createFolder.mutateAsync(trimmed);
      }
      onClose();
    } catch {
      // Inline error message below (submitError) keeps the sheet open with
      // what the user typed, same pattern as compose-sheet.tsx.
    }
  }

  async function handleDelete() {
    if (!folder) return;
    try {
      await deleteFolder.mutateAsync(folder.id);
      onClose();
    } catch {
      setConfirmingDelete(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-cream md:items-center md:justify-center md:bg-charcoal/40 md:p-6">
      <div className="flex min-h-0 w-full flex-1 flex-col bg-cream md:h-auto md:max-w-sm md:flex-none md:rounded-xl md:border md:border-border md:shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-display text-lg text-charcoal">{isEditing ? "Manage folder" : "New folder"}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted hover:bg-parchment-dim hover:text-charcoal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3 px-4 py-4 md:flex-none">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted">Folder name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_FOLDER_NAME_LENGTH}
              placeholder="e.g. Regulars, Quotes, Fleet accounts"
              autoFocus
              className="h-11 rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-rust"
            />
          </label>

          {submitError && <p className="text-sm text-danger">{errorMessage(submitError, "Something went wrong. Please try again.")}</p>}

          {isEditing && folder && (
            <div className="mt-2 border-t border-border pt-3">
              {confirmingDelete ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-charcoal">
                    Delete &ldquo;{folder.name}&rdquo;? Its mail will move to Archive — nothing is deleted.
                  </p>
                  {deleteFolder.isError && (
                    <p className="text-sm text-danger">{errorMessage(deleteFolder.error, "Couldn't delete this folder.")}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirmingDelete(false)}
                      disabled={deleteFolder.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDelete}
                      disabled={deleteFolder.isPending}
                      className="border-danger text-danger hover:bg-danger/10"
                    >
                      {deleteFolder.isPending ? "Deleting…" : "Yes, delete"}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  className="text-sm font-medium text-danger hover:underline"
                >
                  Delete this folder
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit || saving}>
            {saving ? "Saving…" : isEditing ? "Save" : "Create folder"}
          </Button>
        </div>
      </div>
    </div>
  );
}
